import React, { useState, useReducer, useCallback } from "react";
import "./App.css";

const COLUMNS = ["Todo", "In Progress", "Review", "Done"];

const INITIAL_TASKS = [
  { id: 1, title: "Design new landing page", desc: "Revamp the hero section with updated branding", column: "Todo", priority: "high", label: "Design", due: "2025-05-10", assignee: "Sara" },
  { id: 2, title: "Fix auth bug on login", desc: "JWT token refresh not working on mobile", column: "In Progress", priority: "critical", label: "Bug", due: "2025-04-28", assignee: "Ahmed" },
  { id: 3, title: "Write unit tests for API", desc: "Cover all endpoints with Jest + Supertest", column: "Review", priority: "medium", label: "Testing", due: "2025-05-05", assignee: "Ali" },
  { id: 4, title: "Deploy v2.1 to production", desc: "CI/CD pipeline with GitHub Actions", column: "Done", priority: "high", label: "DevOps", due: "2025-04-20", assignee: "Sara" },
  { id: 5, title: "User onboarding flow", desc: "3-step guided setup for new users", column: "Todo", priority: "medium", label: "Feature", due: "2025-05-15", assignee: "Ahmed" },
  { id: 6, title: "Performance audit", desc: "Lighthouse score improvements", column: "In Progress", priority: "low", label: "Perf", due: "2025-05-01", assignee: "Ali" },
  { id: 7, title: "API rate limiting", desc: "Implement Redis-based rate limiter", column: "Review", priority: "high", label: "Backend", due: "2025-04-30", assignee: "Ahmed" },
  { id: 8, title: "Mobile responsive nav", desc: "Hamburger menu for screens < 768px", column: "Done", priority: "medium", label: "Frontend", due: "2025-04-18", assignee: "Sara" },
];

const PRIORITY_COLOR = { critical: "#e53e3e", high: "#dd6b20", medium: "#d69e2e", low: "#38a169" };
const LABEL_COLOR = { Design: "#805ad5", Bug: "#e53e3e", Testing: "#3182ce", DevOps: "#2b6cb0", Feature: "#38a169", Perf: "#d69e2e", Backend: "#744210", Frontend: "#553c9a" };

function taskReducer(state, action) {
  switch (action.type) {
    case "MOVE": return state.map(t => t.id === action.id ? { ...t, column: action.column } : t);
    case "ADD": return [...state, { ...action.task, id: Date.now() }];
    case "DELETE": return state.filter(t => t.id !== action.id);
    case "EDIT": return state.map(t => t.id === action.task.id ? action.task : t);
    default: return state;
  }
}

function TaskCard({ task, onMove, onDelete, onEdit }) {
  const [dragging, setDragging] = useState(false);
  return (
    <div
      className={`task-card priority-${task.priority} ${dragging ? "dragging" : ""}`}
      draggable
      onDragStart={e => { setDragging(true); e.dataTransfer.setData("taskId", task.id); }}
      onDragEnd={() => setDragging(false)}
    >
      <div className="task-header">
        <span className="label" style={{ background: LABEL_COLOR[task.label] + "22", color: LABEL_COLOR[task.label] }}>{task.label}</span>
        <span className="priority-dot" style={{ background: PRIORITY_COLOR[task.priority] }} title={task.priority} />
      </div>
      <h4 className="task-title">{task.title}</h4>
      <p className="task-desc">{task.desc}</p>
      <div className="task-footer">
        <span className="due">📅 {task.due}</span>
        <div className="avatar" title={task.assignee}>{task.assignee[0]}</div>
      </div>
      <div className="task-actions">
        <select value={task.column} onChange={e => onMove(task.id, e.target.value)} className="move-select">
          {COLUMNS.map(c => <option key={c}>{c}</option>)}
        </select>
        <button onClick={() => onEdit(task)} className="btn-icon">✏️</button>
        <button onClick={() => onDelete(task.id)} className="btn-icon">🗑️</button>
      </div>
    </div>
  );
}

function Column({ name, tasks, onMove, onDelete, onEdit }) {
  const [over, setOver] = useState(false);
  return (
    <div
      className={`column ${over ? "col-over" : ""}`}
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { setOver(false); onMove(+e.dataTransfer.getData("taskId"), name); }}
    >
      <div className="col-header">
        <span className="col-name">{name}</span>
        <span className="col-count">{tasks.length}</span>
      </div>
      <div className="col-body">
        {tasks.map(t => <TaskCard key={t.id} task={t} onMove={onMove} onDelete={onDelete} onEdit={onEdit} />)}
        {tasks.length === 0 && <div className="empty-col">Drop tasks here</div>}
      </div>
    </div>
  );
}

function Analytics({ tasks }) {
  const done = tasks.filter(t => t.column === "Done").length;
  const total = tasks.length;
  const byPriority = ["critical", "high", "medium", "low"].map(p => ({ p, count: tasks.filter(t => t.priority === p).length }));
  const byAssignee = [...new Set(tasks.map(t => t.assignee))].map(a => ({ a, done: tasks.filter(t => t.assignee === a && t.column === "Done").length, total: tasks.filter(t => t.assignee === a).length }));

  return (
    <div className="analytics">
      <div className="stat-row">
        <div className="stat-card"><div className="stat-num">{total}</div><div className="stat-label">Total Tasks</div></div>
        <div className="stat-card"><div className="stat-num" style={{ color: "#38a169" }}>{done}</div><div className="stat-label">Completed</div></div>
        <div className="stat-card"><div className="stat-num" style={{ color: "#3182ce" }}>{tasks.filter(t => t.column === "In Progress").length}</div><div className="stat-label">In Progress</div></div>
        <div className="stat-card"><div className="stat-num" style={{ color: "#d69e2e" }}>{Math.round((done / total) * 100)}%</div><div className="stat-label">Completion Rate</div></div>
      </div>
      <div className="charts-row">
        <div className="chart-box">
          <h4>Tasks by Priority</h4>
          {byPriority.map(({ p, count }) => (
            <div key={p} className="bar-row">
              <span className="bar-label">{p}</span>
              <div className="bar-track"><div className="bar-fill" style={{ width: `${(count / total) * 100}%`, background: PRIORITY_COLOR[p] }} /></div>
              <span className="bar-num">{count}</span>
            </div>
          ))}
        </div>
        <div className="chart-box">
          <h4>Team Performance</h4>
          {byAssignee.map(({ a, done, total: t }) => (
            <div key={a} className="bar-row">
              <div className="avatar sm" style={{ marginRight: 8 }}>{a[0]}</div>
              <span className="bar-label">{a}</span>
              <div className="bar-track"><div className="bar-fill" style={{ width: `${t ? (done / t) * 100 : 0}%`, background: "#3182ce" }} /></div>
              <span className="bar-num">{done}/{t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TaskModal({ task, onSave, onClose }) {
  const [form, setForm] = useState(task || { title: "", desc: "", column: "Todo", priority: "medium", label: "Feature", due: "", assignee: "" });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>{task ? "Edit Task" : "New Task"}</h3>
        <label>Title<input value={form.title} onChange={set("title")} placeholder="Task title..." /></label>
        <label>Description<textarea value={form.desc} onChange={set("desc")} rows={3} placeholder="Task description..." /></label>
        <div className="form-row">
          <label>Priority
            <select value={form.priority} onChange={set("priority")}>
              {["critical", "high", "medium", "low"].map(p => <option key={p}>{p}</option>)}
            </select>
          </label>
          <label>Label
            <select value={form.label} onChange={set("label")}>
              {Object.keys(LABEL_COLOR).map(l => <option key={l}>{l}</option>)}
            </select>
          </label>
        </div>
        <div className="form-row">
          <label>Column
            <select value={form.column} onChange={set("column")}>
              {COLUMNS.map(c => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label>Assignee<input value={form.assignee} onChange={set("assignee")} placeholder="Name..." /></label>
        </div>
        <label>Due Date<input type="date" value={form.due} onChange={set("due")} /></label>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={() => { if (form.title) { onSave(form); onClose(); } }}>
            {task ? "Update Task" : "Create Task"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [tasks, dispatch] = useReducer(taskReducer, INITIAL_TASKS);
  const [view, setView] = useState("board");
  const [search, setSearch] = useState("");
  const [filterPriority, setFilterPriority] = useState("all");
  const [modal, setModal] = useState(null);

  const filtered = tasks.filter(t =>
    (filterPriority === "all" || t.priority === filterPriority) &&
    (t.title.toLowerCase().includes(search.toLowerCase()) || t.label.toLowerCase().includes(search.toLowerCase()))
  );

  const handleMove = useCallback((id, column) => dispatch({ type: "MOVE", id, column }), []);
  const handleDelete = useCallback((id) => dispatch({ type: "DELETE", id }), []);
  const handleSave = useCallback((task) => dispatch({ type: task.id ? "EDIT" : "ADD", task, id: task.id }), []);

  return (
    <div className="app">
      <header className="topbar">
        <div className="logo">⚡ TaskFlow</div>
        <div className="nav">
          <button className={view === "board" ? "nav-btn active" : "nav-btn"} onClick={() => setView("board")}>📋 Board</button>
          <button className={view === "analytics" ? "nav-btn active" : "nav-btn"} onClick={() => setView("analytics")}>📊 Analytics</button>
        </div>
        <div className="topbar-right">
          <input className="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks..." />
          <select className="filter-select" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
            <option value="all">All priorities</option>
            {["critical", "high", "medium", "low"].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <button className="btn-primary" onClick={() => setModal({ new: true })}>+ New Task</button>
        </div>
      </header>

      <main className="main">
        {view === "board" && (
          <div className="board">
            {COLUMNS.map(col => (
              <Column
                key={col}
                name={col}
                tasks={filtered.filter(t => t.column === col)}
                onMove={handleMove}
                onDelete={handleDelete}
                onEdit={t => setModal({ edit: t })}
              />
            ))}
          </div>
        )}
        {view === "analytics" && <Analytics tasks={tasks} />}
      </main>

      {(modal?.new || modal?.edit) && (
        <TaskModal
          task={modal.edit || null}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
